export type PdfThumbnailQueue = {
  run: <Value>(
    task: () => Promise<Value>,
    signal?: AbortSignal,
  ) => Promise<Value>;
  cancel: () => void;
};

export const createPdfThumbnailQueue = (concurrency = 3): PdfThumbnailQueue => {
  type Entry = {
    run: () => Promise<unknown>;
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
    signal?: AbortSignal;
  };

  let activeCount = 0;
  let cancelled = false;
  const entries: Entry[] = [];

  const pump = () => {
    if (cancelled) return;
    while (activeCount < concurrency && entries.length > 0) {
      const entry = entries.shift();
      if (!entry) return;
      if (entry.signal?.aborted) {
        entry.reject(new DOMException('Aborted', 'AbortError'));
        continue;
      }
      activeCount += 1;
      void entry
        .run()
        .then(entry.resolve, entry.reject)
        .finally(() => {
          activeCount -= 1;
          pump();
        });
    }
  };

  return {
    run(task, signal) {
      if (cancelled || signal?.aborted) {
        return Promise.reject(new DOMException('Aborted', 'AbortError'));
      }
      return new Promise((resolve, reject) => {
        entries.push({
          run: task,
          resolve: resolve as (value: unknown) => void,
          reject,
          signal,
        });
        pump();
      });
    },
    cancel() {
      cancelled = true;
      for (const entry of entries.splice(0)) {
        entry.reject(new DOMException('Aborted', 'AbortError'));
      }
    },
  };
};
